import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  createSharedRouteTariff,
  suggestSharedRouteTariff,
} from "../services/estimatePricingService.js";

const TARIFF_BASIS_OPTIONS = [
  { value: "PER_100_FT3", label: "Per 100 ft³" },
  { value: "PER_FT3", label: "Per ft³" },
];

const SEASON_OPTIONS = [
  "standard",
  "mid_month",
  "month_end",
  "off_peak",
  "peak",
];

function displayNumber(value, options = {}) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("en-ZA", options);
}

function faceRateFromPer100(ratePer100, basis) {
  const amount = Number(ratePer100);
  if (!Number.isFinite(amount)) return "";
  return basis === "PER_FT3" ? amount / 100 : amount;
}

const SharedRouteTariffDrawer = ({
  open,
  onClose,
  estimateResult,
  onCreated,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const geography = estimateResult?.geography || {};
  const pricingSeason = estimateResult?.estimate?.pricingSeason;
  const originRegion =
    geography.originPricingRegion || geography.originProvince || "";
  const destinationRegion =
    geography.destinationPricingRegion || geography.destinationProvince || "";
  const initialSeason =
    pricingSeason?.seasonCode || estimateResult?.seasonCode || "standard";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [basis, setBasis] = useState("");
  const [season, setSeason] = useState(initialSeason);
  const [rate, setRate] = useState("");
  const [mileageRate, setMileageRate] = useState("");
  const [corridorDistanceKm, setCorridorDistanceKm] = useState("");
  const [distanceDirty, setDistanceDirty] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  const loadSuggestion = useCallback(async (distanceOverride) => {
    if (!originRegion || !destinationRegion) return;
    setLoading(true);
    setError(null);
    try {
      const data = await suggestSharedRouteTariff({
        originRegion,
        destinationRegion,
        seasonCode: initialSeason,
        ...(Number(distanceOverride) > 0
          ? { corridorDistanceKm: Number(distanceOverride) }
          : {}),
      });
      setSuggestion(data);
      const nextBasis = data?.tariffBasis || "";
      setBasis(nextBasis);
      setSeason(data?.seasonCode || initialSeason);
      setRate(
        data?.available
          ? String(faceRateFromPer100(data.suggestedRatePer100Ft3, nextBasis))
          : ""
      );
      setMileageRate(
        data?.suggestedMileageRate != null
          ? String(data.suggestedMileageRate)
          : ""
      );
      setCorridorDistanceKm(
        data?.targetDistanceKm != null ? String(data.targetDistanceKm) : ""
      );
      setDistanceDirty(false);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          requestError?.message ||
          "Could not calculate a tariff suggestion"
      );
    } finally {
      setLoading(false);
    }
  }, [destinationRegion, initialSeason, originRegion]);

  useEffect(() => {
    if (!open) return;
    setSuggestion(null);
    setError(null);
    setEvidenceOpen(false);
    setSeason(initialSeason);
    setCorridorDistanceKm("");
    setDistanceDirty(false);
    loadSuggestion();
  }, [open, initialSeason, loadSuggestion]);

  const handleBasisChange = (nextBasis) => {
    setBasis(nextBasis);
    if (suggestion?.suggestedRatePer100Ft3 != null) {
      setRate(
        String(
          faceRateFromPer100(
            suggestion.suggestedRatePer100Ft3,
            nextBasis
          )
        )
      );
    }
  };

  const canSave =
    Number(rate) > 0 &&
    Number(corridorDistanceKm) > 0 &&
    !distanceDirty &&
    Boolean(basis) &&
    Boolean(suggestion?.catalogueRevision) &&
    Boolean(originRegion) &&
    Boolean(destinationRegion);

  const handleSave = async () => {
    if (!canSave) {
      setError("Enter a positive tariff and confirm its basis.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createSharedRouteTariff({
        originRegion,
        destinationRegion,
        season,
        rate: Number(rate),
        tariffBasis: basis,
        mileageRate:
          Number(mileageRate) > 0 ? Number(mileageRate) : undefined,
        distanceKm: Number(corridorDistanceKm),
        catalogueRevision: suggestion?.catalogueRevision,
        enabled: true,
        notes: suggestion?.available
          ? `Created from estimate drawer using ${suggestion.method}; ${suggestion.sampleCount} comparable route(s)`
          : "Created manually from estimate drawer",
      });
      await onCreated?.();
      onClose();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          requestError?.message ||
          "Could not save the shared-load tariff"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateDistance = async () => {
    const distance = Number(corridorDistanceKm);
    if (!Number.isFinite(distance) || distance <= 0) {
      setError("Enter a positive hub-to-hub corridor distance.");
      return;
    }
    await loadSuggestion(distance);
  };

  const suggestedCustomerLineHaul = useMemo(() => {
    const hundreds = Number(estimateResult?.estimate?.billableHundredsFt3);
    const per100 = Number(suggestion?.suggestedRatePer100Ft3);
    return Number.isFinite(hundreds) && Number.isFinite(per100)
      ? hundreds * per100
      : null;
  }, [estimateResult, suggestion]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: { width: fullScreen ? "100%" : 520, maxWidth: "100%" },
        "data-testid": "shared-route-tariff-drawer",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Create shared-load tariff
        </Typography>
        <IconButton
          aria-label="Close tariff drawer"
          onClick={onClose}
          disabled={saving}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack spacing={2} sx={{ p: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Route
          </Typography>
          <Typography variant="h6" data-testid="tariff-route-heading">
            {originRegion || "?"} → {destinationRegion || "?"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live card: {suggestion?.cardId || "Loading…"} · Season: {season}
          </Typography>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={30} />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && suggestion?.available && (
          <Alert severity="info" data-testid="tariff-suggestion">
            <Stack spacing={0.75}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={700}>
                  Calculated from the live card
                </Typography>
                <Chip
                  size="small"
                  label={`${suggestion.confidence} confidence`}
                />
              </Stack>
              <Typography variant="h5" component="p">
                R {displayNumber(suggestion.suggestedRatePer100Ft3, {
                  maximumFractionDigits: 2,
                })}{" "}
                per 100 ft³
              </Typography>
              <Typography variant="body2">
                Hub-to-hub corridor distance:{" "}
                {displayNumber(suggestion.targetDistanceKm, {
                  maximumFractionDigits: 1,
                })}{" "}
                km
                {suggestion.distanceSource
                  ? ` (${suggestion.distanceSource.replace(/_/g, " ")})`
                  : ""}
              </Typography>
              {suggestedCustomerLineHaul != null && (
                <Typography variant="body2">
                  This lead’s estimated line haul: R{" "}
                  {displayNumber(suggestedCustomerLineHaul, {
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {!loading && suggestion && !suggestion.available && (
          <Alert severity="warning">
            {suggestion.message} Enter a reviewed tariff manually to continue.
          </Alert>
        )}

        {!loading && suggestion?.available && (
          <Box>
            <Button
              size="small"
              onClick={() => setEvidenceOpen((value) => !value)}
              endIcon={
                evidenceOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
            >
              {evidenceOpen
                ? "Hide calculation evidence"
                : `View ${suggestion.sampleCount} comparable route${
                    suggestion.sampleCount === 1 ? "" : "s"
                  }`}
            </Button>
            <Collapse in={evidenceOpen}>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {(suggestion.rationale || []).map((line) => (
                  <Typography key={line} variant="body2">
                    • {line}
                  </Typography>
                ))}
                {(suggestion.comparables || []).map((row) => (
                  <Box
                    key={row.id}
                    sx={{ p: 1.25, bgcolor: "action.hover", borderRadius: 1 }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {row.originRegion} → {row.destinationRegion}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {displayNumber(row.distanceKm, {
                        maximumFractionDigits: 1,
                      })}{" "}
                      km · R{" "}
                      {displayNumber(row.ratePer100Ft3, {
                        maximumFractionDigits: 2,
                      })}
                      /100 ft³ · distance-adjusted R{" "}
                      {displayNumber(row.normalizedRatePer100Ft3, {
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        <Divider />

        <Typography variant="subtitle1" fontWeight={700}>
          Review tariff
        </Typography>
        <TextField
          label="Hub-to-hub corridor distance (km)"
          value={corridorDistanceKm}
          onChange={(event) => {
            setCorridorDistanceKm(event.target.value);
            setDistanceDirty(true);
          }}
          inputProps={{
            inputMode: "decimal",
            "data-testid": "shared-route-corridor-distance",
          }}
          helperText="Line-haul distance only; excludes collection and delivery legs."
          fullWidth
        />
        {distanceDirty && (
          <Button
            variant="outlined"
            onClick={handleRecalculateDistance}
            disabled={loading}
            data-testid="shared-route-recalculate"
          >
            Recalculate suggestion
          </Button>
        )}
        <TextField
          label="Route tariff"
          value={rate}
          onChange={(event) => setRate(event.target.value)}
          inputProps={{
            inputMode: "decimal",
            "data-testid": "shared-route-tariff-rate",
          }}
          helperText="Editable suggestion; this value is not activated until saved."
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel id="shared-route-tariff-basis-label">
            Tariff basis
          </InputLabel>
          <Select
            labelId="shared-route-tariff-basis-label"
            label="Tariff basis"
            value={basis}
            onChange={(event) => handleBasisChange(event.target.value)}
            inputProps={{ "data-testid": "shared-route-tariff-basis" }}
          >
            {TARIFF_BASIS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Collection/delivery mileage rate (R/km)"
          value={mileageRate}
          onChange={(event) => setMileageRate(event.target.value)}
          inputProps={{ inputMode: "decimal" }}
          helperText="Used for depot collection and delivery legs; line haul remains capacity-based."
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel id="shared-route-season-label">Season</InputLabel>
          <Select
            labelId="shared-route-season-label"
            label="Season"
            value={season}
            onChange={(event) => setSeason(event.target.value)}
          >
            {SEASON_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Alert severity="warning" variant="outlined">
          Saving activates this directed tariff on the live card. The reverse
          direction must be assessed separately.
        </Alert>

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          gap={1}
          justifyContent="flex-end"
        >
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading || !canSave}
            data-testid="shared-route-tariff-save"
          >
            {saving ? "Saving…" : "Save & re-estimate"}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default SharedRouteTariffDrawer;
